import { useAuth } from "@/features/auth/hooks/useAuth";
import { useEffect, useRef, useState } from "react";

export interface MapProgress {
  total: number;
  completed: number;
  failed: number;
  in_progress: number;
  started_at: number | null;
}

/**
 * Poll for map operation progress during tool execution.
 * The inference engine writes map_progress.json during parallel map operations.
 */
export function useMapProgress(
  sessionId: string | undefined,
  enabled: boolean,
  intervalMs = 1500,
): MapProgress | null {
  const { getIdToken } = useAuth();
  const [progress, setProgress] = useState<MapProgress | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hadProgressRef = useRef(false);

  useEffect(() => {
    if (!enabled || !sessionId) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setProgress(null);
      hadProgressRef.current = false;
      return;
    }

    const poll = async () => {
      try {
        const token = await getIdToken();
        const res = await fetch(
          `/api/chat/map-progress`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) return;
        const data = await res.json();

        if (data.status === "idle" || !data.total) {
          if (hadProgressRef.current) {
            setProgress(null);
            hadProgressRef.current = false;
          }
          return;
        }

        hadProgressRef.current = true;
        setProgress({
          total: data.total,
          completed: data.completed,
          failed: data.failed,
          in_progress: data.in_progress,
          started_at: data.started_at ?? null,
        });
      } catch {
        // Silently ignore — keep last known progress (don't flicker on transient errors)
      }
    };

    poll();
    timerRef.current = setInterval(poll, intervalMs);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, sessionId, intervalMs, getIdToken]);

  return progress;
}
